import type { Deliverable, Epic, PersonRef, ProductGoal } from './types.js';

/** Token meaning "(Blanks)" inside a multi-select filter. */
export const BLANK = '__blank__';

/**
 * Sentinel selection meaning "nothing is selected": a multi-select filter holding only this value
 * matches no deliverable (an empty array means "no filter" instead). Lets the filter editor start with
 * everything selected and let the user deselect down to a subset, Excel-style.
 */
export const NONE = '__none__';

export interface FilterState {
  /** Global search: matches deliverable, epic and goal names. */
  q: string;
  /** Name column text filter. */
  name: string;
  /** Product Goal ids a deliverable is linked to (BLANK = no goal). */
  goals: string[];
  /** Epic ids a deliverable is linked to (BLANK = standalone). */
  epics: string[];
  productAreas: string[];
  /** Type labels (external / internal; BLANK = no type). */
  types: string[];
  /** person ids */
  pmOwners: string[];
  assignees: string[];
  sprints: string[];
  scopingStatuses: string[];
  statuses: string[];
  scopingDueFrom: string | null;
  scopingDueTo: string | null;
}

export function emptyFilters(): FilterState {
  return {
    q: '',
    name: '',
    goals: [],
    epics: [],
    productAreas: [],
    types: [],
    pmOwners: [],
    assignees: [],
    sprints: [],
    scopingStatuses: [],
    statuses: [],
    scopingDueFrom: null,
    scopingDueTo: null,
  };
}

export type FilterKey = keyof FilterState;

/** Number of active column filters (the search box `q` is counted separately by the UI). */
export function activeFilterCount(f: FilterState): number {
  let n = 0;
  if (f.name.trim()) n++;
  for (const k of ['goals', 'epics', 'productAreas', 'types', 'pmOwners', 'assignees', 'sprints', 'scopingStatuses', 'statuses'] as const) {
    if (f[k].length > 0) n++;
  }
  if (f.scopingDueFrom || f.scopingDueTo) n++;
  return n;
}

export function isFiltersActive(f: FilterState): boolean {
  return activeFilterCount(f) > 0 || f.q.trim().length > 0;
}

export interface FilterContext {
  epicsById: Map<string, Epic>;
  goalsById: Map<string, ProductGoal>;
}

export function makeFilterContext(epics: Epic[], goals: ProductGoal[]): FilterContext {
  return { epicsById: new Map(epics.map((e) => [e.id, e])), goalsById: new Map(goals.map((g) => [g.id, g])) };
}

const lc = (s: string) => s.toLowerCase();

function scalarMatch(selected: string[], value: string | null): boolean {
  if (selected.length === 0) return true;
  return selected.includes(value ?? BLANK);
}

/** Matches when any linked id is selected; no links match only when BLANK is selected. */
function linkMatch(selected: string[], ids: string[]): boolean {
  if (selected.length === 0) return true;
  if (ids.length === 0) return selected.includes(BLANK);
  return ids.some((id) => selected.includes(id));
}

function peopleMatch(selected: string[], people: PersonRef[]): boolean {
  if (selected.length === 0) return true;
  if (people.length === 0) return selected.includes(BLANK);
  return people.some((p) => selected.includes(p.id));
}

export function matchesFilters(d: Deliverable, f: FilterState, ctx: FilterContext): boolean {
  const q = lc(f.q.trim());
  if (q) {
    const haystack = [
      d.name,
      d.key ?? '',
      ...d.epicIds.map((id) => ctx.epicsById.get(id)?.name ?? ''),
      ...d.productGoalIds.map((id) => ctx.goalsById.get(id)?.name ?? ''),
    ];
    if (!haystack.some((h) => lc(h).includes(q))) return false;
  }
  const name = lc(f.name.trim());
  if (name && !lc(d.name).includes(name)) return false;
  if (!linkMatch(f.goals, d.productGoalIds)) return false;
  if (!linkMatch(f.epics, d.epicIds)) return false;
  if (!scalarMatch(f.productAreas, d.productArea)) return false;
  if (!scalarMatch(f.types, d.type)) return false;
  if (!peopleMatch(f.pmOwners, d.pmOwners)) return false;
  if (!peopleMatch(f.assignees, d.assignees)) return false;
  if (!scalarMatch(f.sprints, d.sprint)) return false;
  if (!scalarMatch(f.scopingStatuses, d.scopingStatus)) return false;
  if (!scalarMatch(f.statuses, d.status)) return false;
  if (f.scopingDueFrom || f.scopingDueTo) {
    if (!d.scopingDue) return false;
    if (f.scopingDueFrom && d.scopingDue < f.scopingDueFrom) return false;
    if (f.scopingDueTo && d.scopingDue > f.scopingDueTo) return false;
  }
  return true;
}

/** Distinct values (with counts) for a column, for the "Excel-like" multi-select menus. */
export function distinctValues(
  deliverables: Deliverable[],
  pick: (d: Deliverable) => Array<{ value: string; label: string }>,
): Array<{ value: string; label: string; count: number }> {
  const map = new Map<string, { value: string; label: string; count: number }>();
  for (const d of deliverables) {
    const picked = pick(d);
    const entries = picked.length > 0 ? picked : [{ value: BLANK, label: '(Blanks)' }];
    for (const e of entries) {
      const cur = map.get(e.value);
      if (cur) cur.count++;
      else map.set(e.value, { ...e, count: 1 });
    }
  }
  return [...map.values()];
}

// ---- URL state -----------------------------------------------------------------------------

const ARRAY_KEYS = ['goals', 'epics', 'productAreas', 'types', 'pmOwners', 'assignees', 'sprints', 'scopingStatuses', 'statuses'] as const;
const PARAM: Record<(typeof ARRAY_KEYS)[number], string> = {
  goals: 'goal',
  epics: 'epic',
  productAreas: 'area',
  types: 'type',
  pmOwners: 'pm',
  assignees: 'asg',
  sprints: 'sprint',
  scopingStatuses: 'scoping',
  statuses: 'status',
};

export function filtersToSearchParams(f: FilterState, into: URLSearchParams = new URLSearchParams()): URLSearchParams {
  const set = (k: string, v: string | null) => {
    into.delete(k);
    if (v) into.set(k, v);
  };
  set('q', f.q.trim() || null);
  set('name', f.name.trim() || null);
  for (const key of ARRAY_KEYS) set(PARAM[key], f[key].length ? f[key].join(',') : null);
  set('dueFrom', f.scopingDueFrom);
  set('dueTo', f.scopingDueTo);
  return into;
}

export function filtersFromSearchParams(p: URLSearchParams): FilterState {
  const f = emptyFilters();
  f.q = p.get('q') ?? '';
  f.name = p.get('name') ?? '';
  for (const key of ARRAY_KEYS) {
    const raw = p.get(PARAM[key]);
    f[key] = raw ? raw.split(',').filter(Boolean) : [];
  }
  f.scopingDueFrom = p.get('dueFrom');
  f.scopingDueTo = p.get('dueTo');
  return f;
}
